import { Link } from "react-router-dom";

export interface HeroContent {
  badge?: string;
  headline: string;
  headlineAccent?: string;
  subheadline: string;
  description: string;
  primaryCta: string;
  primaryCtaPath: string;
  secondaryCta?: string;
  secondaryCtaPath?: string;
  backgroundImage: string;
  overlayIntensity?: string;
}

interface HeroSectionProps {
  content: HeroContent;
}

export default function HeroSection({ content }: HeroSectionProps) {
  const {
    badge,
    headline,
    headlineAccent,
    subheadline,
    description,
    primaryCta,
    primaryCtaPath,
    secondaryCta,
    secondaryCtaPath,
    backgroundImage,
  } = content;

  return (
    <section className="relative w-full min-h-screen flex items-center overflow-hidden bg-[#0A0A0A]">
      {/* Background with Ken Burns */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat ken-burns will-change-transform"
          style={{ backgroundImage: `url(${backgroundImage})` }}
        />
        {/* Multi-layer cinematic overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/65 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/70" />
        {/* Vignette */}
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 30% 50%, transparent 30%, rgba(0,0,0,0.55) 100%)" }} />
        {/* Subtle warm tint */}
        <div className="absolute inset-0 bg-[#3A2A10]/12" />
      </div>

      {/* Left accent line */}
      <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-transparent via-[#C9A961]/50 to-transparent z-10" />

      {/* Vertical text — right side */}
      <div className="absolute right-8 top-1/2 -translate-y-1/2 hidden xl:flex flex-col items-center gap-4 z-10">
        <div
          className="text-white/15 text-[9px] tracking-[0.45em] uppercase"
          style={{ fontFamily: "'Inter', sans-serif", writingMode: "vertical-rl", transform: "rotate(180deg)" }}
        >
          Boca Raton · South Florida · Since 2010
        </div>
        <div className="w-px h-24 bg-gradient-to-b from-transparent via-white/15 to-transparent" />
      </div>

      {/* KRP watermark — decorative */}
      <div
        className="absolute right-16 bottom-20 text-white/[0.03] text-[18vw] font-bold leading-none select-none hidden lg:block"
        style={{ fontFamily: "'Playfair Display', serif" }}
        aria-hidden="true"
      >
        KRP
      </div>

      {/* Main content */}
      <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 xl:px-10 pt-40 pb-32">
        <div className="max-w-[720px]">

          {/* Badge */}
          {badge && (
            <div className="hero-anim-1 flex items-center gap-4 mb-10">
              <div className="h-px w-16 bg-gradient-to-r from-[#C9A961] to-[#C9A961]/40" />
              <span
                className="text-[#C9A961] text-[10px] tracking-[0.3em] uppercase font-medium"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {badge}
              </span>
            </div>
          )}

          {/* Main headline */}
          <h1
            className="hero-anim-2 text-[56px] md:text-[72px] xl:text-[92px] text-white leading-[0.96] mb-6"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, letterSpacing: "-0.02em" }}
          >
            {headline}
            {headlineAccent && (
              <em
                className="block text-[#C9A961]"
                style={{ fontStyle: "italic", fontFamily: "'Playfair Display', serif" }}
              >
                {headlineAccent}
              </em>
            )}
          </h1>

          {/* Thin gold rule */}
          <div className="hero-anim-3 flex items-center gap-5 mb-6">
            <div className="h-px w-10 bg-[#C9A961]" />
            <p
              className="text-white/65 text-lg md:text-xl leading-snug"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
            >
              {subheadline}
            </p>
          </div>

          {/* Description */}
          <p
            className="hero-anim-3 text-white/40 text-[13px] md:text-sm mb-12 leading-relaxed max-w-lg"
            style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300, letterSpacing: "0.02em" }}
          >
            {description}
          </p>

          {/* CTA Buttons */}
          <div className="hero-anim-4 flex flex-wrap gap-4 mb-16">
            <Link
              to={primaryCtaPath}
              className="group relative overflow-hidden bg-[#C9A961] text-white text-[10px] tracking-[0.22em] uppercase font-semibold px-12 py-5 hover:bg-[#b8964f] transition-all duration-500 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <span className="relative z-10">{primaryCta}</span>
              <div className="absolute inset-0 bg-white/10 -translate-x-full group-hover:translate-x-0 transition-transform duration-500 skew-x-12" />
            </Link>
            {secondaryCta && secondaryCtaPath && (
              <Link
                to={secondaryCtaPath}
                className="border border-white/30 text-white text-[10px] tracking-[0.22em] uppercase font-medium px-12 py-5 hover:border-[#C9A961] hover:text-[#C9A961] transition-all duration-400 cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {secondaryCta}
              </Link>
            )}
          </div>

          {/* Proof strip */}
          <div className="hero-anim-5 flex flex-wrap items-stretch gap-8">
            <div className="flex flex-col justify-center border-l-[2px] border-[#C9A961] pl-5">
              <div className="flex gap-0.5 mb-1">
                {[1,2,3,4,5].map((s) => (
                  <i key={s} className="ri-star-fill text-[#C9A961] text-[10px]" />
                ))}
              </div>
              <p className="text-white/85 text-sm font-semibold" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: "0.02em" }}>
                4.9 / 5.0
              </p>
              <p className="text-white/35 text-[10px] mt-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>
                150+ Verified Reviews
              </p>
            </div>

            <div className="w-px bg-white/10 self-stretch hidden sm:block" />

            <div className="flex flex-col justify-center border-l-[2px] border-[#C9A961]/50 pl-5">
              <p className="text-white/85 text-sm font-semibold" style={{ fontFamily: "'Inter', sans-serif" }}>
                Showroom Open
              </p>
              <p className="text-white/35 text-[10px] mt-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>
                Boca Center · Mon–Fri 10–5
              </p>
            </div>

            <div className="w-px bg-white/10 self-stretch hidden sm:block" />

            <div className="flex flex-col justify-center border-l-[2px] border-[#C9A961]/50 pl-5">
              <p className="text-white/85 text-sm font-semibold" style={{ fontFamily: "'Inter', sans-serif" }}>
                500+ Projects
              </p>
              <p className="text-white/35 text-[10px] mt-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>
                Across South Florida
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 z-10">
        <div className="w-[1px] h-14 bg-gradient-to-b from-white/30 to-transparent scroll-bounce" />
        <span className="text-white/25 text-[8px] tracking-[0.4em] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
          Scroll
        </span>
      </div>
    </section>
  );
}
